import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import AdminLayout from '../components/AdminLayout';
import api from '../services/api';

interface WalletRuleRow {
  rule_key: string;
  label: string;
  amount_inr: number;
  helper_text?: string;
  is_core: boolean;
  is_enabled: boolean;
  display_order: number;
}

const NEW_RULE: WalletRuleRow = {
  rule_key: '',
  label: '',
  amount_inr: 0,
  helper_text: '',
  is_core: false,
  is_enabled: true,
  display_order: 999,
};

const AdminWalletRulesPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [rows, setRows] = useState<WalletRuleRow[]>([]);

  const normalizeKey = (rawValue: string) => rawValue.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/^_+|_+$/g, '');

  const loadRules = async () => {
    try {
      setLoading(true);
      const response = await api.get('/wallet/admin/rules');
      setRows(response.data.items || []);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load account rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadRules();
  }, []);

  const updateRow = (index: number, patch: Partial<WalletRuleRow>) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addRule = () => {
    setRows((prev) => [...prev, { ...NEW_RULE, display_order: (prev.length + 1) * 10 }]);
  };

  const removeRule = (index: number) => {
    const target = rows[index];
    if (target.is_core) {
      toast.error('Core rules cannot be removed');
      return;
    }
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const saveRules = async () => {
    const invalid = rows.find((row) => {
      const generatedKey = normalizeKey(row.rule_key || row.label || '');
      return !generatedKey || !row.label.trim() || Number(row.amount_inr) < 0;
    });
    if (invalid) {
      toast.error('Each rule needs key, label and valid amount');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        items: rows.map((row, index) => ({
          ...row,
          rule_key: normalizeKey(row.rule_key || row.label),
          label: row.label.trim(),
          amount_inr: Number(row.amount_inr),
          display_order: (index + 1) * 10,
        })),
      };
      const response = await api.put('/wallet/admin/rules', payload);
      setRows(response.data.items || []);
      toast.success('Account rules updated');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save account rules');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout currentPage="wallet-rules">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Account Rules
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure wallet pricing rows shown to users. Core rules are minimum recharge and view contact.
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Add new rows, update amounts, disable optional rows, or remove non-core rows anytime.
      </Alert>

      <Box sx={{ mb: 1.5 }}>
        <FormControlLabel
          control={<Switch checked={showAdvanced} onChange={(e) => setShowAdvanced(e.target.checked)} />}
          label="Advanced mode (show technical rule keys and helper text)"
        />
      </Box>

      {loading ? (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {rows.map((row, index) => (
            <Card key={`${row.rule_key}-${index}`}>
              <CardContent>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: showAdvanced ? '1fr 1fr 160px 160px auto' : '1.4fr 160px 160px auto' }, gap: 1.25 }}>
                  {showAdvanced && (
                    <TextField
                      size="small"
                      label="Rule Key"
                      value={row.rule_key}
                      disabled={row.is_core}
                      onChange={(e) => updateRow(index, { rule_key: normalizeKey(e.target.value) })}
                    />
                  )}
                  <TextField
                    size="small"
                    label="Label"
                    value={row.label}
                    onChange={(e) => {
                      const nextLabel = e.target.value;
                      const patch: Partial<WalletRuleRow> = { label: nextLabel };
                      if (!showAdvanced && !row.is_core && !row.rule_key.trim()) {
                        patch.rule_key = normalizeKey(nextLabel);
                      }
                      updateRow(index, patch);
                    }}
                  />
                  <TextField
                    size="small"
                    label="Amount (Points)"
                    type="number"
                    inputProps={{ min: 0 }}
                    value={row.amount_inr}
                    onChange={(e) => updateRow(index, { amount_inr: Number(e.target.value || 0) })}
                  />
                  <TextField
                    size="small"
                    select
                    label="Enabled"
                    value={row.is_enabled ? 'YES' : 'NO'}
                    disabled={row.is_core}
                    onChange={(e) => updateRow(index, { is_enabled: e.target.value === 'YES' })}
                  >
                    <MenuItem value="YES">Yes</MenuItem>
                    <MenuItem value="NO">No</MenuItem>
                  </TextField>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                    {row.is_core && <Chip size="small" color="primary" label="Core" />}
                    {!showAdvanced && !row.is_core && row.rule_key && <Chip size="small" variant="outlined" label={`Key: ${row.rule_key}`} />}
                    <Button color="error" variant="outlined" onClick={() => removeRule(index)} disabled={row.is_core}>
                      Remove
                    </Button>
                  </Stack>
                </Box>

                {showAdvanced && (
                  <TextField
                    size="small"
                    label="Helper Text"
                    fullWidth
                    sx={{ mt: 1.25 }}
                    value={row.helper_text || ''}
                    onChange={(e) => updateRow(index, { helper_text: e.target.value })}
                  />
                )}
              </CardContent>
            </Card>
          ))}

          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addRule}>
              Add Rule
            </Button>
            <Button variant="contained" onClick={saveRules} disabled={saving}>
              {saving ? 'Saving...' : 'Save Rules'}
            </Button>
          </Stack>
        </Stack>
      )}
    </AdminLayout>
  );
};

export default AdminWalletRulesPage;
